import { css } from 'styled-components';

// https://www.gatsbyjs.org/packages/gatsby-remark-prismjs

const PrismStyles = css`
  /**
  * Enhanced code block container with improved styling
  */
  .gatsby-highlight {
    background-color: var(--prism-bg);
    color: var(--prism-text);
    border-radius: var(--border-radius);
    margin: 2em 0;
    padding: 1.5em;
    overflow: auto;
    position: relative;
    font-family: var(--font-mono);
    font-size: var(--fz-md);
    border: 1px solid var(--border);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    /* Better scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: var(--lightest-navy) var(--prism-bg);

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: var(--prism-bg);
    }

    &::-webkit-scrollbar-thumb {
      background: var(--lightest-navy);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: var(--light-slate);
    }

    @media (max-width: 768px) {
      padding: 1.25em;
      border-radius: 8px;
      margin: 1.5em 0;
    }

    @media (max-width: 480px) {
      padding: 1em;
      border-radius: 6px;
      margin: 1.25em 0;
    }
  }

  .gatsby-highlight code[class*='language-'],
  .gatsby-highlight pre[class*='language-'] {
    height: auto !important;
    font-size: var(--fz-sm);
    line-height: 1.6; /* Better readability */
    white-space: pre;
    word-spacing: normal;
    word-break: normal;
    word-wrap: normal;
    tab-size: 2;
    hyphens: none;
    font-weight: 400;
    letter-spacing: 0.02em; /* Slight letter spacing for better readability */

    @media (max-width: 768px) {
      font-size: var(--fz-xs);
      line-height: 1.7;
    }
  }

  /**
  * Remove the default PrismJS theme background-color, border-radius, margin,
  * padding and overflow.
  * 1. Make the element just wide enough to fit its content.
  * 2. Always fill the visible space in .gatsby-highlight.
  * 3. Adjust the position of the line numbers
  */
  .gatsby-highlight pre[class*='language-'] {
    background-color: transparent;
    margin: 0;
    padding: 0;
    overflow: initial;
    float: left; /* 1 */
    min-width: 100%; /* 2 */
    padding-top: 2em;
  }

  /* Enhanced file names */
  .gatsby-code-title {
    padding: 1em 1.5em;
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 600;
    background-color: var(--lightest-navy);
    color: var(--lightest-slate);
    border: 1px solid var(--border);
    border-bottom: none;
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
    position: relative;

    /* File icon */
    &:before {
      content: '📄';
      margin-right: 0.5em;
      opacity: 0.7;
    }

    & + .gatsby-highlight {
      margin-top: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-top: none;
    }

    @media (max-width: 768px) {
      padding: 0.8em 1.25em;
    }

    @media (max-width: 480px) {
      padding: 0.7em 1em;
      font-size: var(--fz-xxs);
    }
  }

  /* Enhanced line highlighting */
  .gatsby-highlight-code-line {
    display: block;
    background-color: var(--prism-line-highlight);
    border-left: 3px solid var(--green);
    padding-left: calc(1em + 3px);
    padding-right: 1em;
    margin-right: -1.5em;
    margin-left: -1.5em;
    position: relative;

    /* Subtle glow effect */
    box-shadow: inset 0 0 0 1px rgba(100, 255, 218, 0.1);

    @media (max-width: 768px) {
      margin-right: -1.25em;
      margin-left: -1.25em;
      padding-left: calc(0.8em + 3px);
    }

    @media (max-width: 480px) {
      margin-right: -1em;
      margin-left: -1em;
      padding-left: calc(0.6em + 3px);
    }
  }

  /* Enhanced language badges */
  .gatsby-highlight pre[class*='language-']::before {
    background: var(--green);
    color: var(--navy);
    font-size: var(--fz-xxs);
    font-family: var(--font-mono);
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    border-radius: 0 0 6px 6px;
    position: absolute;
    top: 0;
    left: 1.25rem;
    padding: 0.3rem 0.6rem;
    z-index: 2;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      left: 1rem;
      padding: 0.25rem 0.5rem;
    }

    @media (max-width: 480px) {
      left: 0.8rem;
      padding: 0.2rem 0.4rem;
      font-size: 10px;
    }
  }
  .gatsby-highlight pre[class='language-javascript']::before {
    content: 'js';
  }
  .gatsby-highlight pre[class='language-js']::before {
    content: 'js';
  }
  .gatsby-highlight pre[class='language-jsx']::before {
    content: 'jsx';
  }
  .gatsby-highlight pre[class='language-graphql']::before {
    content: 'GraphQL';
  }
  .gatsby-highlight pre[class='language-html']::before {
    content: 'html';
  }
  .gatsby-highlight pre[class='language-css']::before {
    content: 'css';
  }
  .gatsby-highlight pre[class='language-mdx']::before {
    content: 'mdx';
  }
  .gatsby-highlight pre[class='language-shell']::before {
    content: 'shell';
  }
  .gatsby-highlight pre[class='language-sh']::before {
    content: 'sh';
  }
  .gatsby-highlight pre[class='language-bash']::before {
    content: 'bash';
  }
  .gatsby-highlight pre[class='language-yaml']::before {
    content: 'yaml';
  }
  .gatsby-highlight pre[class='language-markdown']::before {
    content: 'md';
  }
  .gatsby-highlight pre[class='language-json']::before,
  .gatsby-highlight pre[class='language-json5']::before {
    content: 'json';
  }
  .gatsby-highlight pre[class='language-diff']::before {
    content: 'diff';
  }
  .gatsby-highlight pre[class='language-text']::before {
    content: 'text';
  }
  .gatsby-highlight pre[class='language-flow']::before {
    content: 'flow';
  }
  .gatsby-highlight pre[class='language-typescript']::before,
  .gatsby-highlight pre[class='language-ts']::before {
    content: 'ts';
  }
  .gatsby-highlight pre[class='language-tsx']::before {
    content: 'tsx';
  }
  .gatsby-highlight pre[class='language-python']::before,
  .gatsby-highlight pre[class='language-py']::before {
    content: 'python';
  }
  .gatsby-highlight pre[class='language-php']::before {
    content: 'php';
  }
  .gatsby-highlight pre[class='language-java']::before {
    content: 'java';
  }
  .gatsby-highlight pre[class='language-c']::before {
    content: 'c';
  }
  .gatsby-highlight pre[class='language-cpp']::before {
    content: 'c++';
  }
  .gatsby-highlight pre[class='language-sql']::before {
    content: 'sql';
  }
  .gatsby-highlight pre[class='language-ruby']::before,
  .gatsby-highlight pre[class='language-rb']::before {
    content: 'ruby';
  }
  .gatsby-highlight pre[class='language-go']::before {
    content: 'go';
  }
  .gatsby-highlight pre[class='language-rust']::before {
    content: 'rust';
  }
  .gatsby-highlight pre[class='language-swift']::before {
    content: 'swift';
  }
  .gatsby-highlight pre[class='language-kotlin']::before {
    content: 'kotlin';
  }

  /* Prism Styles */
  .token {
    display: inline;
  }
  .token.comment,
  .token.block-comment,
  .token.prolog,
  .token.doctype,
  .token.cdata {
    color: var(--prism-comment);
  }
  .token.punctuation {
    color: var(--prism-text);
  }
  .token.namespace,
  .token.deleted {
    color: var(--prism-red);
  }
  .token.function-name,
  .token.function,
  .token.class-name,
  .token.constant,
  .token.symbol {
    color: var(--prism-yellow);
  }
  .token.attr-name,
  .token.operator,
  .token.rule {
    color: var(--prism-orange);
  }
  .token.keyword,
  .token.boolean,
  .token.number,
  .token.property {
    color: var(--prism-purple);
  }
  .token.tag,
  .token.selector,
  .token.important,
  .token.atrule,
  .token.builtin,
  .token.entity,
  .token.url {
    color: var(--prism-blue);
  }
  .token.string,
  .token.char,
  .token.attr-value,
  .token.regex,
  .token.variable,
  .token.inserted {
    color: var(--prism-green);
  }
  .token.important,
  .token.bold {
    font-weight: 600;
  }
  .token.italic {
    font-style: italic;
  }
  .token.entity {
    cursor: help;
  }
  .namespace {
    opacity: 0.7;
  }
`;

export default PrismStyles;
